import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Search, Plus, Trash2, Save, Send, X, AlertTriangle, Package, Building2, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { purchaseOrdersAPI, vendorsAPI, warehousesAPI, productsAPI } from '@/services/api';
import api from '@/services/api';

const POCreatePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingPO, setExistingPO] = useState(null);
  
  // Master data
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loadingLowStock, setLoadingLowStock] = useState(false);
  
  // Search states
  const [vendorSearch, setVendorSearch] = useState('');
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [showWarehouseDropdown, setShowWarehouseDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  
  // Refs for click outside handling
  const vendorRef = useRef(null);
  const warehouseRef = useRef(null);
  const productRef = useRef(null);
  
  // Form data
  const [formData, setFormData] = useState({
    vendor_id: '',
    vendor_name: '',
    vendor_code: '',
    destination_type: 'warehouse', // 'warehouse' or 'store'
    warehouse_id: '',
    warehouse_name: '',
    store_id: '',
    store_name: '',
    expected_delivery_date: '',
    notes: '',
    items: []
  });
  
  // Stores list
  const [stores, setStores] = useState([]);
  const [storeSearch, setStoreSearch] = useState('');
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const storeRef = useRef(null);
  
  const { toast } = useToast();

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorRef.current && !vendorRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
      if (warehouseRef.current && !warehouseRef.current.contains(event.target)) {
        setShowWarehouseDropdown(false);
      }
      if (storeRef.current && !storeRef.current.contains(event.target)) {
        setShowStoreDropdown(false);
      }
      if (productRef.current && !productRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch master data
  const fetchMasterData = useCallback(async () => {
    try {
      const [vendorsRes, warehousesRes, storesRes, productsRes] = await Promise.all([
        vendorsAPI.list({ is_active: true }),
        warehousesAPI.list({ is_active: true }),
        api.get('/stores?is_active=true'),
        productsAPI.list({ is_active: true })
      ]);
      setVendors(vendorsRes.data || []);
      setWarehouses(warehousesRes.data || []);
      setStores(storesRes.data || []);
      setProducts(productsRes.data?.items || productsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    }
  }, []);

  // Fetch products by selected supplier
  const fetchSupplierProducts = useCallback(async (supplierId) => {
    if (!supplierId) {
      setSupplierProducts([]);
      return;
    }
    try {
      const response = await productsAPI.list({ supplier_id: supplierId, is_active: true, page_size: 500 });
      setSupplierProducts(response.data?.items || response.data || []);
    } catch (error) {
      console.error('Failed to fetch supplier products:', error);
      setSupplierProducts([]);
    }
  }, []);

  // Fetch existing PO if editing
  const fetchPO = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await purchaseOrdersAPI.get(id);
      const po = response.data;
      setExistingPO(po);
      setFormData({
        vendor_id: po.vendor_id,
        vendor_name: po.vendor_name,
        vendor_code: po.vendor_code,
        destination_type: po.destination_type || 'warehouse',
        warehouse_id: po.warehouse_id || '',
        warehouse_name: po.warehouse_name || '',
        store_id: po.store_id || '',
        store_name: po.store_name || '',
        expected_delivery_date: po.expected_delivery_date ? po.expected_delivery_date.split('T')[0] : '',
        notes: po.notes || '',
        items: po.items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          product_sku: item.product_sku,
          ordered_qty: item.ordered_qty,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent || 0,
          tax_rate: item.tax_rate || 0
        }))
      });
      setVendorSearch(po.vendor_name);
      setWarehouseSearch(po.warehouse_name);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load PO', variant: 'destructive' });
      navigate('/hq/purchase-orders');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toast]);

  useEffect(() => {
    fetchMasterData();
    if (id) fetchPO();
  }, [fetchMasterData, fetchPO, id]);

  // Filtered lists for search
  const filteredVendors = vendors.filter(v => 
    vendorSearch && (
      v.name.toLowerCase().includes(vendorSearch.toLowerCase()) ||
      v.code.toLowerCase().includes(vendorSearch.toLowerCase())
    )
  ).slice(0, 8);

  const filteredWarehouses = warehouses.filter(w => 
    warehouseSearch && (
      w.name.toLowerCase().includes(warehouseSearch.toLowerCase()) ||
      w.code.toLowerCase().includes(warehouseSearch.toLowerCase())
    )
  ).slice(0, 8);

  // Use supplier-specific products if vendor selected, otherwise show all
  const productListForSearch = formData.vendor_id && supplierProducts.length > 0 
    ? supplierProducts 
    : products;
    
  const filteredProducts = productListForSearch.filter(p => 
    productSearch && (
      p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.sku.toLowerCase().includes(productSearch.toLowerCase())
    )
  ).slice(0, 10);

  // Handlers
  const handleVendorSelect = async (vendor) => {
    setFormData(prev => ({
      ...prev,
      vendor_id: vendor.id,
      vendor_name: vendor.name,
      vendor_code: vendor.code
    }));
    setVendorSearch(vendor.name);
    setShowVendorDropdown(false);
    
    // Fetch products mapped to this supplier
    try {
      const response = await productsAPI.getBySupplier(vendor.id);
      setSupplierProducts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch supplier products:', error);
      setSupplierProducts([]);
    }
    
    // Fetch low-stock products for this supplier
    setLoadingLowStock(true);
    try {
      const lowStockRes = await productsAPI.getLowStock(vendor.id);
      setLowStockProducts(lowStockRes.data?.low_stock_products || []);
    } catch (error) {
      console.error('Failed to fetch low-stock products:', error);
      setLowStockProducts([]);
    } finally {
      setLoadingLowStock(false);
    }
  };

  const handleWarehouseSelect = (warehouse) => {
    setFormData(prev => ({
      ...prev,
      warehouse_id: warehouse.id,
      warehouse_name: warehouse.name
    }));
    setWarehouseSearch(warehouse.name);
    setShowWarehouseDropdown(false);
  };

  const handleStoreSelect = (store) => {
    setFormData(prev => ({
      ...prev,
      store_id: store.id,
      store_name: store.name
    }));
    setStoreSearch(store.name);
    setShowStoreDropdown(false);
  };

  const handleDestinationTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      destination_type: type,
      warehouse_id: '',
      warehouse_name: '',
      store_id: '',
      store_name: ''
    }));
    setWarehouseSearch('');
    setStoreSearch('');
  };

  // Filtered stores for search
  const filteredStores = stores.filter(s =>
    s.name?.toLowerCase().includes(storeSearch.toLowerCase()) ||
    s.code?.toLowerCase().includes(storeSearch.toLowerCase())
  ).slice(0, 8);

  // Fetch last purchase price for a product from current vendor
  const fetchVendorPurchaseHistory = async (productId) => {
    if (!formData.vendor_id) return null;
    try {
      const response = await api.get(`/api/products/${productId}/vendor-purchase-history`, {
        params: { vendor_id: formData.vendor_id }
      });
      return response.data;
    } catch (error) {
      console.log('No purchase history found');
      return null;
    }
  };

  const handleAddProduct = async (product) => {
    const exists = formData.items.find(i => i.product_id === product.id);
    if (exists) {
      toast({ title: 'Info', description: 'Product already added' });
      return;
    }
    
    // Use product tax_rate if available, otherwise fallback to 18
    // Note: tax_rate of 0 is valid for tax-exempt products
    const taxRate = product.tax_rate !== undefined && product.tax_rate !== null 
      ? product.tax_rate 
      : 18;
    
    // Fetch last purchase price from this vendor
    const purchaseHistory = await fetchVendorPurchaseHistory(product.id);
    
    // Use vendor-specific last purchase price if available, else fallback to product's stored last_purchase_price
    const unitPrice = purchaseHistory?.found 
      ? purchaseHistory.last_rate 
      : (product.last_purchase_price || product.current_price?.cost_price || product.cost_price || 0);
    
    const newItem = {
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      ordered_qty: 1,
      unit_price: unitPrice,
      discount_percent: 0,
      tax_rate: taxRate,
      // Vendor-specific purchase history
      last_purchase_price: purchaseHistory?.found ? purchaseHistory.last_rate : (product.last_purchase_price || null),
      last_purchase_date: purchaseHistory?.found ? purchaseHistory.grn_date : (product.last_purchase_date || null),
      last_purchase_grn: purchaseHistory?.found ? purchaseHistory.grn_number : null,
      last_purchase_mrp: purchaseHistory?.found ? purchaseHistory.mrp : null
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    
    // Show toast if last purchase price was found
    if (purchaseHistory?.found) {
      toast({ 
        title: 'Last Purchase Found', 
        description: `₹${purchaseHistory.last_rate} from GRN: ${purchaseHistory.grn_number}`,
        duration: 3000
      });
    }
    
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const handleUpdateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: parseFloat(value) || 0 } : item
      )
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Auto-add low-stock items to PO
  const handleAutoAddLowStock = () => {
    if (lowStockProducts.length === 0) {
      toast({ title: 'Info', description: 'No low-stock items found for this supplier', variant: 'default' });
      return;
    }
    
    let addedCount = 0;
    const newItems = [...formData.items];
    
    lowStockProducts.forEach(lowStockItem => {
      // Skip if already in the list
      const exists = newItems.find(i => i.product_id === lowStockItem.product_id);
      if (exists) return;
      
      // Use product tax_rate if available, otherwise fallback to 18
      const taxRate = lowStockItem.tax_rate !== undefined && lowStockItem.tax_rate !== null 
        ? lowStockItem.tax_rate 
        : 18;
      
      newItems.push({
        product_id: lowStockItem.product_id,
        product_name: lowStockItem.name,
        product_sku: lowStockItem.sku,
        ordered_qty: Math.ceil(lowStockItem.suggested_reorder_qty) || 1,
        unit_price: lowStockItem.cost_price || 0,
        discount_percent: 0,
        tax_rate: taxRate,
        _lowStock: true  // Mark as auto-added
      });
      addedCount++;
    });
    
    if (addedCount > 0) {
      setFormData(prev => ({ ...prev, items: newItems }));
      toast({ 
        title: 'Low-Stock Items Added', 
        description: `${addedCount} item(s) with low stock have been added to the order. You can adjust quantities or remove items.` 
      });
    } else {
      toast({ title: 'Info', description: 'All low-stock items are already in the order' });
    }
  };

  // Calculations
  const calculateItemTotal = (item) => {
    const subtotal = item.ordered_qty * item.unit_price;
    const discount = subtotal * (item.discount_percent / 100);
    const taxable = subtotal - discount;
    const tax = taxable * (item.tax_rate / 100);
    return taxable + tax;
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let discountTotal = 0;
    let taxTotal = 0;

    formData.items.forEach(item => {
      const lineSubtotal = item.ordered_qty * item.unit_price;
      const lineDiscount = lineSubtotal * (item.discount_percent / 100);
      const lineTaxable = lineSubtotal - lineDiscount;
      const lineTax = lineTaxable * (item.tax_rate / 100);

      subtotal += lineSubtotal;
      discountTotal += lineDiscount;
      taxTotal += lineTax;
    });

    return {
      subtotal: subtotal.toFixed(2),
      discountTotal: discountTotal.toFixed(2),
      taxTotal: taxTotal.toFixed(2),
      grandTotal: (subtotal - discountTotal + taxTotal).toFixed(2)
    };
  };

  const totals = calculateTotals();

  // Save handlers
  const handleSave = async (submit = false) => {
    if (!formData.vendor_id) {
      toast({ title: 'Validation Error', description: 'Please select a vendor', variant: 'destructive' });
      return;
    }
    
    // Validate destination based on type
    if (formData.destination_type === 'warehouse' && !formData.warehouse_id) {
      toast({ title: 'Validation Error', description: 'Please select a warehouse', variant: 'destructive' });
      return;
    }
    if (formData.destination_type === 'store' && !formData.store_id) {
      toast({ title: 'Validation Error', description: 'Please select a store', variant: 'destructive' });
      return;
    }
    
    if (formData.items.length === 0) {
      toast({ title: 'Validation Error', description: 'Add at least one item', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        vendor_id: formData.vendor_id,
        destination_type: formData.destination_type,
        warehouse_id: formData.destination_type === 'warehouse' ? formData.warehouse_id : null,
        store_id: formData.destination_type === 'store' ? formData.store_id : null,
        expected_delivery_date: formData.expected_delivery_date ? new Date(formData.expected_delivery_date).toISOString() : null,
        notes: formData.notes,
        items: formData.items
      };

      let po;
      if (isEditMode) {
        const response = await purchaseOrdersAPI.update(id, payload);
        po = response.data;
        toast({ title: 'Success', description: 'Purchase Order updated successfully' });
      } else {
        const response = await purchaseOrdersAPI.create(payload);
        po = response.data;
        toast({ title: 'Success', description: `Purchase Order ${po.po_number} created successfully` });
      }

      if (submit && po) {
        await purchaseOrdersAPI.submit(po.id);
        toast({ title: 'Success', description: 'PO submitted for approval' });
      }

      navigate('/hq/purchase-orders');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/hq/purchase-orders')} className="h-10 w-10">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-800">
            {isEditMode ? `Edit Purchase Order ${existingPO?.po_number || ''}` : 'Create Purchase Order'}
          </h1>
          <p className="text-gray-500">
            {isEditMode ? 'Modify the purchase order details' : 'Create a new purchase order for vendors'}
          </p>
        </div>
        {existingPO && (
          <Badge className="bg-gray-100 text-gray-700">{existingPO.status?.replace('_', ' ').toUpperCase()}</Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vendor & Warehouse Selection */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg text-gray-800">Order Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Vendor Search */}
                <div className="space-y-2" ref={vendorRef}>
                  <Label className="text-gray-700">Vendor *</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search vendor by name or code..."
                      value={vendorSearch}
                      onChange={(e) => {
                        setVendorSearch(e.target.value);
                        setShowVendorDropdown(true);
                        if (!e.target.value) {
                          setFormData(prev => ({ ...prev, vendor_id: '', vendor_name: '', vendor_code: '' }));
                        }
                      }}
                      onFocus={() => setShowVendorDropdown(true)}
                      className="pl-10 bg-gray-50 border-gray-300"
                      data-testid="vendor-search"
                    />
                    {showVendorDropdown && filteredVendors.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredVendors.map(v => (
                          <div
                            key={v.id}
                            className="px-4 py-2 hover:bg-teal-50 cursor-pointer flex justify-between items-center"
                            onClick={() => handleVendorSelect(v)}
                          >
                            <div>
                              <p className="text-gray-800 font-medium">{v.name}</p>
                              <p className="text-gray-500 text-xs">Balance: ₹{(v.current_balance || 0).toLocaleString()}</p>
                            </div>
                            <span className="text-teal-600 text-sm font-medium">{v.code}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {formData.vendor_id && (
                    <p className="text-xs text-teal-600">Selected: {formData.vendor_code}</p>
                  )}
                </div>

                {/* Destination Type Selector */}
                <div className="space-y-2">
                  <Label className="text-gray-700">Destination Type *</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.destination_type === 'warehouse' ? 'default' : 'outline'}
                      className={formData.destination_type === 'warehouse' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                      onClick={() => handleDestinationTypeChange('warehouse')}
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      Warehouse
                    </Button>
                    <Button
                      type="button"
                      variant={formData.destination_type === 'store' ? 'default' : 'outline'}
                      className={formData.destination_type === 'store' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                      onClick={() => handleDestinationTypeChange('store')}
                    >
                      <Store className="w-4 h-4 mr-2" />
                      Store
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Warehouse Search - Show only if destination is warehouse */}
                {formData.destination_type === 'warehouse' && (
                  <div className="space-y-2" ref={warehouseRef}>
                    <Label className="text-gray-700">Warehouse *</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search warehouse by name or code..."
                        value={warehouseSearch}
                        onChange={(e) => {
                          setWarehouseSearch(e.target.value);
                          setShowWarehouseDropdown(true);
                          if (!e.target.value) {
                            setFormData(prev => ({ ...prev, warehouse_id: '', warehouse_name: '' }));
                          }
                        }}
                        onFocus={() => setShowWarehouseDropdown(true)}
                        className="pl-10 bg-gray-50 border-gray-300"
                        data-testid="warehouse-search"
                      />
                      {showWarehouseDropdown && filteredWarehouses.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                          {filteredWarehouses.map(w => (
                            <div
                              key={w.id}
                              className="px-4 py-2 hover:bg-teal-50 cursor-pointer flex justify-between items-center"
                              onClick={() => handleWarehouseSelect(w)}
                            >
                              <span className="text-gray-800">{w.name}</span>
                              <span className="text-teal-600 text-sm">{w.code}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {formData.warehouse_id && (
                      <p className="text-xs text-teal-600">Selected: {formData.warehouse_name}</p>
                    )}
                  </div>
                )}

                {/* Store Search - Show only if destination is store */}
                {formData.destination_type === 'store' && (
                  <div className="space-y-2" ref={storeRef}>
                    <Label className="text-gray-700">Store *</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search store by name or code..."
                        value={storeSearch}
                        onChange={(e) => {
                          setStoreSearch(e.target.value);
                          setShowStoreDropdown(true);
                          if (!e.target.value) {
                            setFormData(prev => ({ ...prev, store_id: '', store_name: '' }));
                          }
                        }}
                        onFocus={() => setShowStoreDropdown(true)}
                        className="pl-10 bg-gray-50 border-gray-300"
                        data-testid="store-search"
                      />
                      {showStoreDropdown && filteredStores.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                          {filteredStores.map(s => (
                            <div
                              key={s.id}
                              className="px-4 py-2 hover:bg-teal-50 cursor-pointer flex justify-between items-center"
                              onClick={() => handleStoreSelect(s)}
                            >
                              <span className="text-gray-800">{s.name}</span>
                              <span className="text-teal-600 text-sm">{s.code}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {formData.store_id && (
                      <p className="text-xs text-teal-600">Selected: {formData.store_name}</p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-gray-700">Expected Delivery Date</Label>
                  <Input
                    type="date"
                    value={formData.expected_delivery_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes..."
                  className="bg-gray-50 border-gray-300"
                  rows={1}
                />
              </div>
            </CardContent>
          </Card>

          {/* Product Search & Items */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-gray-800">Order Items</CardTitle>
                {formData.vendor_id && lowStockProducts.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAutoAddLowStock}
                    disabled={loadingLowStock}
                    className="border-amber-500 text-amber-600 hover:bg-amber-50"
                    data-testid="auto-add-lowstock-btn"
                  >
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Add {lowStockProducts.length} Low-Stock Items
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Supplier Product Notice */}
              {formData.vendor_id && (
                <div className={`p-3 rounded-lg border ${supplierProducts.length > 0 ? 'bg-teal-50 border-teal-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-center gap-2">
                    <Package className={`w-4 h-4 ${supplierProducts.length > 0 ? 'text-teal-600' : 'text-amber-600'}`} />
                    <span className={`text-sm ${supplierProducts.length > 0 ? 'text-teal-700' : 'text-amber-700'}`}>
                      {supplierProducts.length > 0 
                        ? `Showing ${supplierProducts.length} products mapped to ${formData.vendor_name}` 
                        : `No products mapped to ${formData.vendor_name}. Showing all products.`}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Product Search */}
              <div className="space-y-2" ref={productRef}>
                <Label className="text-gray-700">Add Products</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder={formData.vendor_id && supplierProducts.length > 0 
                      ? `Search ${formData.vendor_name}'s products...` 
                      : "Search products by name or SKU..."}
                    value={productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value);
                      setShowProductDropdown(true);
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                    className="pl-10 bg-gray-50 border-gray-300"
                    data-testid="product-search"
                  />
                  {showProductDropdown && filteredProducts.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-auto">
                      {filteredProducts.map(p => (
                        <div
                          key={p.id}
                          className="px-4 py-3 hover:bg-teal-50 cursor-pointer border-b border-gray-100 last:border-0"
                          onClick={() => handleAddProduct(p)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-gray-800 font-medium">{p.name}</p>
                              <p className="text-gray-500 text-xs">{p.sku}</p>
                            </div>
                            <div className="text-right">
                              {p.last_purchase_price ? (
                                <>
                                  <p className="text-teal-600 font-medium">₹{p.last_purchase_price.toLocaleString()}</p>
                                  <p className="text-emerald-500 text-xs">Last Purchase</p>
                                </>
                              ) : (
                                <>
                                  <p className="text-gray-600 font-medium">₹{p.current_price?.cost_price || p.cost_price || 0}</p>
                                  <p className="text-gray-400 text-xs">Cost Price</p>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Items Table */}
              {formData.items.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50 border-gray-200">
                        <TableHead className="text-gray-600 font-semibold">#</TableHead>
                        <TableHead className="text-gray-600 font-semibold">Product</TableHead>
                        <TableHead className="text-gray-600 font-semibold w-24">Qty</TableHead>
                        <TableHead className="text-gray-600 font-semibold w-28">Rate (₹)</TableHead>
                        <TableHead className="text-gray-600 font-semibold w-20">Disc %</TableHead>
                        <TableHead className="text-gray-600 font-semibold w-20">Tax %</TableHead>
                        <TableHead className="text-gray-600 font-semibold w-28 text-right">Amount (₹)</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index} className={`border-gray-200 ${item._lowStock ? 'bg-amber-50' : ''}`}>
                          <TableCell className="text-gray-500">{index + 1}</TableCell>
                          <TableCell>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-gray-800 font-medium">{item.product_name}</p>
                                {item._lowStock && (
                                  <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1 py-0">Low Stock</Badge>
                                )}
                              </div>
                              <p className="text-gray-500 text-xs">{item.product_sku}</p>
                              {item.last_purchase_price && (
                                <div className="mt-1 p-1.5 bg-emerald-50 rounded border border-emerald-100">
                                  <p className="text-emerald-700 text-xs font-medium">
                                    Last Purchase: ₹{item.last_purchase_price.toLocaleString()}
                                    {item.last_purchase_mrp && <span className="text-gray-500 ml-1">(MRP: ₹{item.last_purchase_mrp})</span>}
                                  </p>
                                  {item.last_purchase_grn && (
                                    <p className="text-emerald-600 text-[10px]">
                                      GRN: {item.last_purchase_grn} 
                                      {item.last_purchase_date && ` | ${new Date(item.last_purchase_date).toLocaleDateString('en-IN')}`}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="1"
                              value={item.ordered_qty}
                              onChange={(e) => handleUpdateItem(index, 'ordered_qty', e.target.value)}
                              className="bg-gray-50 border-gray-300 h-8 text-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => handleUpdateItem(index, 'unit_price', e.target.value)}
                              className="bg-gray-50 border-gray-300 h-8 text-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={item.discount_percent}
                              onChange={(e) => handleUpdateItem(index, 'discount_percent', e.target.value)}
                              className="bg-gray-50 border-gray-300 h-8 text-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={item.tax_rate}
                              onChange={(e) => handleUpdateItem(index, 'tax_rate', e.target.value)}
                              className="bg-gray-50 border-gray-300 h-8 text-sm"
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium text-gray-800">
                            {calculateItemTotal(item).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleRemoveItem(index)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 border border-dashed border-gray-300 rounded-lg">
                  <Plus className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>Search and add products above</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Summary Sidebar */}
        <div className="space-y-6">
          <Card className="bg-white border-gray-200 shadow-sm sticky top-6">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800">Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total Items</span>
                  <span className="text-gray-800 font-medium">{formData.items.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total Qty</span>
                  <span className="text-gray-800 font-medium">
                    {formData.items.reduce((sum, i) => sum + i.ordered_qty, 0)}
                  </span>
                </div>
                <hr className="border-gray-200" />
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-gray-800">₹{totals.subtotal}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Discount</span>
                  <span className="text-red-600">-₹{totals.discountTotal}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Tax (GST)</span>
                  <span className="text-gray-800">₹{totals.taxTotal}</span>
                </div>
                <hr className="border-gray-200" />
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-gray-800">Grand Total</span>
                  <span className="text-teal-600">₹{totals.grandTotal}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 space-y-3">
                <Button
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white"
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  data-testid="save-po-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save as Draft'}
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-teal-500 text-teal-600 hover:bg-teal-50"
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  data-testid="save-submit-po-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Save & Submit for Approval
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-gray-500"
                  onClick={() => navigate('/hq/purchase-orders')}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Vendor Info Card */}
          {formData.vendor_id && (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-4">
                <h4 className="text-amber-800 font-semibold mb-2">Vendor Information</h4>
                <div className="space-y-1 text-sm">
                  <p className="text-amber-700"><span className="text-amber-600">Name:</span> {formData.vendor_name}</p>
                  <p className="text-amber-700"><span className="text-amber-600">Code:</span> {formData.vendor_code}</p>
                  {vendors.find(v => v.id === formData.vendor_id)?.current_balance > 0 && (
                    <p className="text-red-600 font-medium mt-2">
                      Outstanding: ₹{vendors.find(v => v.id === formData.vendor_id)?.current_balance?.toLocaleString()}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Low Stock Alert Card */}
          {formData.vendor_id && lowStockProducts.length > 0 && (
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4">
                <h4 className="text-red-800 font-semibold mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Low Stock Alert
                </h4>
                <p className="text-red-700 text-sm mb-3">
                  {lowStockProducts.length} product(s) from this supplier are below their alert threshold.
                </p>
                <div className="space-y-2 max-h-48 overflow-auto">
                  {lowStockProducts.slice(0, 5).map(item => (
                    <div key={item.product_id} className="text-xs p-2 bg-white rounded border border-red-200">
                      <p className="font-medium text-gray-800 truncate">{item.name}</p>
                      <div className="flex justify-between mt-1 text-gray-600">
                        <span>Stock: {item.total_stock}</span>
                        <span>Alert: {item.low_stock_alert_qty}</span>
                      </div>
                    </div>
                  ))}
                  {lowStockProducts.length > 5 && (
                    <p className="text-xs text-red-600 text-center">+ {lowStockProducts.length - 5} more items</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default POCreatePage;
